import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ArchiveItem from '../../components/ArchiveItem';
import { api } from '../../api/mockData';
import './index.css';

const Archive = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [archives, setArchives] = useState([]);
  const [groupedArchives, setGroupedArchives] = useState({});
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [searchQuery, setSearchQuery] = useState('');
  const [stats, setStats] = useState({
    totalPosts: 0,
    totalMonths: 0,
    years: [],
    startYear: new Date().getFullYear(),
    endYear: new Date().getFullYear()
  });
  const [featuredPosts, setFeaturedPosts] = useState([]);

  useEffect(() => {
    fetchArchiveData();
  }, []);

  const fetchArchiveData = async () => {
    try {
      setLoading(true);
      
      // 获取所有文章
      const response = await api.getPosts({ sort: 'latest', limit: 200 });
      
      if (response.success) {
        const posts = response.data;
        processArchiveData(posts);
        
        // 获取热门文章
        const popularResponse = await api.getPopularPosts({ limit: 3 });
        if (popularResponse.success) {
          setFeaturedPosts(popularResponse.data);
        }
      }
    } catch (error) {
      console.error('加载归档数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const processArchiveData = (posts) => {
    const monthMap = {};
    const yearsSet = new Set();
    
    posts.forEach(post => {
      if (!post.publishDate) return;
      
      const date = new Date(post.publishDate);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const key = `${year}-${month}`;
      
      yearsSet.add(year);
      
      if (!monthMap[key]) {
        monthMap[key] = {
          year,
          month,
          posts: []
        };
      }
      
      monthMap[key].posts.push(post);
    });
    
    // 转换为数组并排序
    const archiveList = Object.values(monthMap)
      .sort((a, b) => {
        if (a.year !== b.year) return b.year - a.year;
        return b.month - a.month;
      });
    
    // 按年份分组
    const grouped = archiveList.reduce((groups, archive) => {
      const year = archive.year;
      if (!groups[year]) {
        groups[year] = [];
      }
      groups[year].push(archive);
      return groups;
    }, {});
    
    // 计算统计
    const totalPosts = posts.length;
    const totalMonths = archiveList.length;
    const years = Array.from(yearsSet).sort((a, b) => b - a);
    const startYear = years.length > 0 ? Math.min(...years) : new Date().getFullYear();
    const endYear = years.length > 0 ? Math.max(...years) : new Date().getFullYear();
    
    setArchives(archiveList);
    setGroupedArchives(grouped);
    setStats({ 
      totalPosts, 
      totalMonths, 
      years, 
      startYear, 
      endYear 
    });
    
    // 默认选中最新年份
    if (years.length > 0) {
      setSelectedYear(years[0]);
    }
  };

  const handleYearClick = (year) => {
    setSelectedYear(year);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      console.log('搜索:', searchQuery);
      // 这里可以实现搜索功能
    }
  };

  // 年份猫咪
  const getYearCat = (year) => {
    const cats = [
      { emoji: '😺', color: '#FF9AA2' },
      { emoji: '😸', color: '#FFB7B2' },
      { emoji: '😹', color: '#FFDAC1' },
      { emoji: '😻', color: '#E2F0CB' },
      { emoji: '😼', color: '#B5EAD7' },
      { emoji: '😽', color: '#C7CEEA' },
      { emoji: '🙀', color: '#D8A7B1' },
      { emoji: '😿', color: '#B8E1DD' },
      { emoji: '😾', color: '#FEC8D8' },
      { emoji: '🐈', color: '#FFDFD3' }
    ];
    const diff = new Date().getFullYear() - year;
    return cats[diff % cats.length] || { emoji: '🐱', color: '#A5D8FF' };
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          <div className="loading-cat">🐱</div>
          <div className="loading-text">正在翻开时光相册...</div>
          <div className="loading-dots">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="archive-page">
      {/* 背景装饰 */}
      <div className="background-cats">
        <span className="cat-1">😺</span>
        <span className="cat-2">😸</span>
        <span className="cat-3">😹</span>
        <span className="cat-4">😻</span>
      </div>

      {/* 页面头部 */}
      <header className="archive-header">
        <div className="header-container">
          <div className="header-left">
            <h1 className="page-title">
              <span className="title-icon">📚</span>
              猫咪时光机
            </h1>
            <p className="page-subtitle">
              记录与猫咪共度的温暖时光，收藏每一份美好回忆
            </p>
          </div>
          <div className="header-right">
            <div className="header-cat">🐈‍⬛</div>
          </div>
        </div>

        {/* 统计卡片 */}
        <div className="stats-section">
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon">📖</div>
              <div className="stat-details">
                <div className="stat-number">{stats.totalPosts}</div>
                <div className="stat-label">篇文章</div>
              </div>
              <div className="stat-cat">🐾</div>
            </div>
            
            <div className="stat-card">
              <div className="stat-icon">📅</div>
              <div className="stat-details">
                <div className="stat-number">{stats.totalMonths}</div>
                <div className="stat-label">个月份</div>
              </div>
              <div className="stat-cat">😺</div>
            </div>
            
            <div className="stat-card">
              <div className="stat-icon">⏳</div>
              <div className="stat-details">
                <div className="stat-number">{stats.years.length}</div>
                <div className="stat-label">个年头</div>
              </div>
              <div className="stat-cat">✨</div>
            </div>
            
            <div className="stat-card">
              <div className="stat-icon">🏮</div>
              <div className="stat-details">
                <div className="stat-range">
                  {stats.startYear} - {stats.endYear}
                </div>
                <div className="stat-label">时光跨度</div>
              </div>
              <div className="stat-cat">🎈</div>
            </div>
          </div>
        </div>
      </header>

      {/* 主要内容区域 */}
      <main className="archive-main">
        <div className="main-container">
          {/* 控制区域 */}
          <div className="controls-section">
            {/* 搜索框 - 改进版 */}
            <div className="search-container">
              <form className="search-box" onSubmit={handleSearch}>
                <div className="search-input-wrapper">
                  <span className="search-icon">🔍</span>
                  <input
                    type="text"
                    placeholder="搜索文章标题、关键词或猫咪名字..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="search-input"
                  />
                  <button type="submit" className="search-button">
                    搜索
                  </button>
                </div>
                <div className="search-hint">
                  <span className="hint-icon">💡</span>
                  试试搜索"猫咪"、"日常"或"趣事"
                </div>
              </form>
            </div>

            {/* 年份导航 */}
            <div className="years-navigation">
              <div className="years-label">📅 选择年份</div>
              <div className="years-scroll">
                {stats.years.map(year => {
                  const yearCat = getYearCat(year);
                  return (
                    <button
                      key={year}
                      className={`year-button ${selectedYear === year ? 'active' : ''}`}
                      onClick={() => handleYearClick(year)}
                      style={{
                        '--year-color': yearCat.color
                      }}
                    >
                      <span className="year-cat">{yearCat.emoji}</span>
                      <span className="year-number">{year}</span>
                      <span className="year-count">
                        {groupedArchives[year]?.length || 0}个月
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* 归档内容 */}
          <div className="content-section">
            {groupedArchives[selectedYear] ? (
              <>
                <div className="section-header">
                  <h2 className="section-title">
                    <span className="title-icon">🎨</span>
                    {selectedYear} 年的故事
                  </h2>
                  <div className="section-stats">
                    <span className="month-count">
                      📅 {groupedArchives[selectedYear].length} 个月份
                    </span>
                    <span className="post-count">
                      📖 {groupedArchives[selectedYear].reduce((sum, archive) => sum + archive.posts.length, 0)} 篇文章
                    </span>
                  </div>
                </div>
                
                <div className="archives-grid">
                  {groupedArchives[selectedYear]
                    .sort((a, b) => b.month - a.month)
                    .map(archive => (
                      <ArchiveItem 
                        key={`${archive.year}-${archive.month}`} 
                        archive={archive} 
                      />
                    ))}
                </div>
              </>
            ) : (
              <div className="empty-state">
                <div className="empty-illustration">
                  <div className="empty-cat">📭</div>
                  <div className="empty-text">
                    <h3>{selectedYear}年还没有故事</h3>
                    <p>这一年猫咪们都在休息呢~ 快来写下第一篇文章吧！</p>
                    <button 
                      className="start-writing"
                      onClick={() => navigate('/write')}
                    >
                      ✏️ 开始记录
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* 热门文章推荐 */}
          {featuredPosts.length > 0 && (
            <div className="featured-section">
              <div className="section-header">
                <h2 className="section-title">
                  <span className="title-icon">⭐</span>
                  热门时光碎片
                </h2>
                <p className="section-subtitle">其他猫咪也喜欢这些故事</p>
              </div>
              
              <div className="featured-grid">
                {featuredPosts.map((post, index) => (
                  <div 
                    key={post.id} 
                    className="featured-card"
                    onClick={() => navigate(`/post/${post.id}`)}
                  >
                    <div className="card-badge">热门</div>
                    <div className="card-image">
                      <img 
                        src={post.coverImage} 
                        alt={post.title}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba?w=800&auto=format&fit=crop';
                        }}
                      />
                    </div>
                    <div className="card-content">
                      <h4 className="card-title">{post.title}</h4>
                      <p className="card-excerpt">{post.excerpt}</p>
                      <div className="card-footer">
                        <span className="card-category">{post.category}</span>
                        <div className="card-meta">
                          <span>👀 {post.views || 0}</span>
                          <span>❤️ {post.likes || 0}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 时光寄语 */}
          <div className="quote-section">
            <div className="quote-content">
              <div className="quote-mark">"</div>
              <p className="quote-text">
                时光就像一只慵懒的猫咪，静静地趴在记忆的窗台上。<br />
                我们用文字记录下与它共度的每一个瞬间，<br />
                这些文字就成了时光给我们的最温暖的礼物。
              </p>
              <div className="quote-author">
                <span className="author-cat">😺</span>
                <span className="author-name">猫咪时光寄语</span>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* 页脚 */}
      <footer className="archive-footer">
        <div className="footer-content">
          <div className="footer-links">
            <button 
              className="footer-link home-link"
              onClick={() => navigate('/')}
            >
              🏠 返回首页
            </button>
            <button 
              className="footer-link write-link"
              onClick={() => navigate('/write')}
            >
              ✏️ 写新文章
            </button>
          </div>
          <div className="footer-copy">
            <p>🐾 © {new Date().getFullYear()} 猫咪时光机 · 每段记忆都值得珍藏 🐾</p>
          </div>
        </div>
      </footer>

      {/* 浮动猫咪 */}
      <div className="floating-elements">
        <div className="floating-cat cat-1">😺</div>
        <div className="floating-cat cat-2">😸</div>
        <div className="floating-cat cat-3">😹</div>
      </div>

      {/* 回到顶部 */}
      <button 
        className="back-to-top"
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      >
        <span className="cat-icon">🐱</span>
        回到顶部
      </button>
    </div>
  );
};

export default Archive;